import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './GitActionsModal.css';

const GitActionsModal = ({
  projectId,
  serviceName,
  repositoryUrl,
  currentBranch,
  currentTag,
  branches = [],
  tags = [],
  hasModifications,
  modifiedFilesCount,
  commitsToPush,
  commitsToPull,
  pipelineStatus,
  onClose,
  onBranchChange,
  onPushComplete,
  onPullComplete,
  onRefresh
}) => {
  const [activeTab, setActiveTab] = useState('changes'); // 'changes', 'branch', 'pipeline', 'quick-actions'
  const [loading, setLoading] = useState(false);

  // Open to Changes tab if there are modifications, otherwise Branch tab
  useEffect(() => {
    if (hasModifications && modifiedFilesCount > 0) {
      setActiveTab('changes');
    }
  }, [hasModifications, modifiedFilesCount]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    if (onRefresh) {
      await onRefresh();
    }
    setLoading(false);
  }, [onRefresh]);

  const getPipelineStatusIcon = () => {
    switch (pipelineStatus) {
      case 'success':
        return '✅';
      case 'failed':
        return '❌';
      case 'running':
        return '🔄';
      case 'pending':
        return '⏳';
      case 'canceled':
        return '🚫';
      case 'skipped':
        return '⏭️';
      default:
        return '⚙️';
    }
  };

  const getPipelineStatusText = () => {
    switch (pipelineStatus) {
      case 'success':
        return 'Pipeline Success';
      case 'failed':
        return 'Pipeline Failed';
      case 'running':
        return 'Pipeline Running';
      case 'pending':
        return 'Pipeline Pending';
      case 'canceled':
        return 'Pipeline Canceled';
      case 'skipped':
        return 'Pipeline Skipped';
      default:
        return 'No Pipeline Status';
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'changes':
        return <ChangesTab 
          hasModifications={hasModifications}
          modifiedFilesCount={modifiedFilesCount}
          commitsToPush={commitsToPush}
          commitsToPull={commitsToPull}
          onPushComplete={onPushComplete}
          onPullComplete={onPullComplete}
        />;
      case 'branch':
        return <BranchTab 
          currentBranch={currentBranch}
          currentTag={currentTag}
          branches={branches}
          tags={tags}
          onBranchChange={onBranchChange}
        />;
      case 'pipeline':
        return <PipelineTab 
          pipelineStatus={pipelineStatus}
          currentBranch={currentBranch}
        />;
      case 'quick-actions':
        return <QuickActionsTab 
          repositoryUrl={repositoryUrl}
          projectId={projectId}
          serviceName={serviceName}
        />;
      default:
        return null;
    }
  };

  return createPortal(
    <div className="git-actions-modal-overlay" onClick={handleOverlayClick}>
      <div className="git-actions-modal">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title">
            <span className="title-icon">🚀</span>
            <span className="title-text">Git Actions</span>
            {loading && <span className="title-loading">⟳</span>}
          </div>
          <div className="modal-status">
            <span className="status-branch">
              {currentBranch ? `🌿 ${currentBranch}` : `🏷️ ${currentTag}`}
            </span>
            <span className={`status-pipeline status-${pipelineStatus || 'none'}`}>
              {getPipelineStatusIcon()} {getPipelineStatusText()}
            </span>
          </div>
          <button className="modal-close" onClick={onClose} title="Close (Esc)">
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button
            className={`tab ${activeTab === 'changes' ? 'active' : ''}`}
            onClick={() => setActiveTab('changes')}
          >
            📝 Changes
            {modifiedFilesCount > 0 && (
              <span className="tab-badge">{modifiedFilesCount}</span>
            )}
          </button>
          <button
            className={`tab ${activeTab === 'branch' ? 'active' : ''}`}
            onClick={() => setActiveTab('branch')}
          >
            🌿 Branch
          </button>
          <button
            className={`tab ${activeTab === 'pipeline' ? 'active' : ''}`}
            onClick={() => setActiveTab('pipeline')}
          >
            {getPipelineStatusIcon()} Pipeline
          </button>
          <button
            className={`tab ${activeTab === 'quick-actions' ? 'active' : ''}`}
            onClick={() => setActiveTab('quick-actions')}
          >
            ⚡ Quick Actions
          </button>
          <button
            className="tab-refresh"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh status"
          >
            {loading ? '⟳' : '🔄'}
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

// Changes Tab Component
const ChangesTab = ({ hasModifications, modifiedFilesCount, commitsToPush, commitsToPull, onPushComplete, onPullComplete }) => {
  return (
    <div className="tab-content changes-tab">
      <div className="status-summary">
        <div className="summary-card">
          <div className="card-icon">📝</div>
          <div className="card-info">
            <div className="card-label">Uncommitted Changes</div>
            <div className="card-value">{modifiedFilesCount || 0} files</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">↑</div>
          <div className="card-info">
            <div className="card-label">To Push</div>
            <div className="card-value">{commitsToPush || 0} commits</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">↓</div>
          <div className="card-info">
            <div className="card-label">To Pull</div>
            <div className="card-value">{commitsToPull || 0} commits</div>
          </div>
        </div>
      </div>

      <div className="tab-section">
        <div className="section-header">
          <h3>Actions</h3>
        </div>
        <div className="action-buttons">
          <button className="action-btn primary" disabled={!commitsToPull}>
            ⬇️ Pull from Remote
          </button>
          <button className="action-btn primary" disabled={!hasModifications && !commitsToPush}>
            ⬆️ Commit & Push
          </button>
          <button className="action-btn">
            🔄 Sync (Pull + Push)
          </button>
        </div>
      </div>

      <div className="tab-section">
        <div className="section-header">
          <h3>Modified Files</h3>
          <span className="section-count">{modifiedFilesCount || 0}</span>
        </div>
        <div className="file-list">
          {modifiedFilesCount > 0 ? (
            <div className="file-list-placeholder">
              <p>File list will be integrated from existing PushModal implementation</p>
              <p className="placeholder-note">• Show modified/deleted files</p>
              <p className="placeholder-note">• Side-by-side diff viewer</p>
              <p className="placeholder-note">• Conflict resolution UI</p>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">✨</div>
              <div className="empty-text">No uncommitted changes</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Branch Tab Component
const BranchTab = ({ currentBranch, currentTag, branches, tags, onBranchChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateBranch, setShowCreateBranch] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');

  const filteredBranches = branches.filter(b => 
    b.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTags = tags.filter(t => 
    t.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="tab-content branch-tab">
      <div className="tab-section">
        <div className="section-header">
          <h3>Current</h3>
        </div>
        <div className="current-ref">
          {currentBranch ? (
            <div className="ref-display">
              <span className="ref-icon">🌿</span>
              <span className="ref-name">{currentBranch}</span>
              <span className="ref-type">branch</span>
            </div>
          ) : (
            <div className="ref-display">
              <span className="ref-icon">🏷️</span>
              <span className="ref-name">{currentTag}</span>
              <span className="ref-type">tag</span>
            </div>
          )}
        </div>
      </div>

      <div className="tab-section">
        <div className="section-header">
          <h3>Actions</h3>
        </div>
        <div className="action-buttons">
          <button 
            className="action-btn primary"
            onClick={() => setShowCreateBranch(!showCreateBranch)}
          >
            ➕ Create Branch
          </button>
          <button className="action-btn">
            🏷️ Create Tag
          </button>
        </div>

        {showCreateBranch && (
          <div className="create-branch-form">
            <input
              type="text"
              className="branch-input"
              placeholder="Enter new branch name..."
              value={newBranchName}
              onChange={(e) => setNewBranchName(e.target.value)}
            />
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setShowCreateBranch(false)}>
                Cancel
              </button>
              <button className="btn-create" disabled={!newBranchName.trim()}>
                Create
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="tab-section">
        <div className="section-header">
          <h3>Switch To</h3>
          <input
            type="text"
            className="search-input"
            placeholder="Search branches/tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="ref-list">
          {filteredBranches.length > 0 && (
            <div className="ref-group">
              <div className="group-label">Branches ({filteredBranches.length})</div>
              {filteredBranches.map((branch) => (
                <button
                  key={branch}
                  className={`ref-item ${branch === currentBranch ? 'active' : ''}`}
                  onClick={() => onBranchChange && onBranchChange(branch, 'branch')}
                >
                  <span className="ref-icon">🌿</span>
                  <span className="ref-name">{branch}</span>
                  {branch === currentBranch && <span className="ref-check">✓</span>}
                </button>
              ))}
            </div>
          )}

          {filteredTags.length > 0 && (
            <div className="ref-group">
              <div className="group-label">Tags ({filteredTags.length})</div>
              {filteredTags.slice(0, 20).map((tag) => (
                <button
                  key={tag}
                  className={`ref-item ${tag === currentTag ? 'active' : ''}`}
                  onClick={() => onBranchChange && onBranchChange(tag, 'tag')}
                >
                  <span className="ref-icon">🏷️</span>
                  <span className="ref-name">{tag}</span>
                  {tag === currentTag && <span className="ref-check">✓</span>}
                </button>
              ))}
              {filteredTags.length > 20 && (
                <div className="ref-more">+{filteredTags.length - 20} more tags</div>
              )}
            </div>
          )}

          {filteredBranches.length === 0 && filteredTags.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <div className="empty-text">No branches or tags found</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Pipeline Tab Component
const PipelineTab = ({ pipelineStatus, currentBranch }) => {
  return (
    <div className="tab-content pipeline-tab">
      <div className="tab-section">
        <div className="section-header">
          <h3>Current Pipeline Status</h3>
        </div>
        <div className={`pipeline-status-card status-${pipelineStatus || 'none'}`}>
          <div className="status-info">
            <div className="status-label">Status</div>
            <div className="status-value">{pipelineStatus || 'No pipeline'}</div>
          </div>
          <div className="status-info">
            <div className="status-label">Branch</div>
            <div className="status-value">{currentBranch || 'N/A'}</div>
          </div>
        </div>
      </div>

      <div className="tab-section">
        <div className="section-header">
          <h3>Actions</h3>
        </div>
        <div className="action-buttons">
          <button className="action-btn primary">
            🚀 Run Pipeline
          </button>
          <button className="action-btn">
            📜 View Pipeline History
          </button>
          <button className="action-btn">
            📊 View Logs
          </button>
        </div>
      </div>

      <div className="tab-section">
        <div className="section-header">
          <h3>Pipeline Integration</h3>
        </div>
        <div className="integration-placeholder">
          <p>Pipeline viewer will be integrated from existing implementation</p>
          <p className="placeholder-note">• Pipeline history list</p>
          <p className="placeholder-note">• Job logs with search</p>
          <p className="placeholder-note">• AI-powered error analysis</p>
        </div>
      </div>
    </div>
  );
};

// Quick Actions Tab Component
const QuickActionsTab = ({ repositoryUrl, projectId, serviceName }) => {
  const handleOpenGitLab = () => {
    if (repositoryUrl) {
      window.open(repositoryUrl, '_blank');
    }
  };

  const handleOpenMiaPlatform = () => {
    // TODO: Construct Mia Platform Console URL
    const consoleUrl = `https://console.mia-platform.eu/projects/${projectId}/design`;
    window.open(consoleUrl, '_blank');
  };

  return (
    <div className="tab-content quick-actions-tab">
      <div className="tab-section">
        <div className="section-header">
          <h3>External Links</h3>
        </div>
        <div className="quick-action-list">
          <button 
            className="quick-action-item"
            onClick={handleOpenGitLab}
            disabled={!repositoryUrl}
          >
            <span className="action-icon">🦊</span>
            <div className="action-info">
              <div className="action-title">Open Repository in GitLab</div>
              <div className="action-description">View source code, issues, and merge requests</div>
            </div>
            <span className="action-arrow">→</span>
          </button>

          <button 
            className="quick-action-item"
            onClick={handleOpenMiaPlatform}
            disabled={!projectId}
          >
            <span className="action-icon">🎨</span>
            <div className="action-info">
              <div className="action-title">Open in Mia-Platform Console</div>
              <div className="action-description">Manage project configuration and deployment</div>
            </div>
            <span className="action-arrow">→</span>
          </button>
        </div>
      </div>

      <div className="tab-section">
        <div className="section-header">
          <h3>Service Info</h3>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <div className="info-label">Service Name</div>
            <div className="info-value">{serviceName || 'N/A'}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Project ID</div>
            <div className="info-value">{projectId || 'N/A'}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Repository URL</div>
            <div className="info-value info-value-truncate">{repositoryUrl || 'N/A'}</div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

GitActionsModal.propTypes = {
  projectId: PropTypes.string,
  serviceName: PropTypes.string,
  repositoryUrl: PropTypes.string,
  currentBranch: PropTypes.string,
  currentTag: PropTypes.string,
  branches: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  hasModifications: PropTypes.bool,
  modifiedFilesCount: PropTypes.number,
  commitsToPush: PropTypes.number,
  commitsToPull: PropTypes.number,
  pipelineStatus: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onBranchChange: PropTypes.func,
  onPushComplete: PropTypes.func,
  onPullComplete: PropTypes.func,
  onRefresh: PropTypes.func
};

ChangesTab.propTypes = {
  hasModifications: PropTypes.bool,
  modifiedFilesCount: PropTypes.number,
  commitsToPush: PropTypes.number,
  commitsToPull: PropTypes.number,
  onPushComplete: PropTypes.func,
  onPullComplete: PropTypes.func
};

BranchTab.propTypes = {
  currentBranch: PropTypes.string,
  currentTag: PropTypes.string,
  branches: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  onBranchChange: PropTypes.func
};

PipelineTab.propTypes = {
  pipelineStatus: PropTypes.string,
  currentBranch: PropTypes.string
};

QuickActionsTab.propTypes = {
  repositoryUrl: PropTypes.string,
  projectId: PropTypes.string,
  serviceName: PropTypes.string
};

export default GitActionsModal;
